// The caller's Company item master, searched (GET /api/companies/items).
//
// THE one feed behind every "point at your own stock" picker in the
// configurator: the kit contents rows, and the kit-or-SKU a rework is working
// on. Each row carries `is_kit` and its component tree, so one list answers both
// questions. Two feeds cannot: any narrower second one makes a partner's plain
// stocked SKU pickable in one picker and invisible in the next.
//
// The backend resolves the Company server-side from the verified token and
// returns a customer-safe subset (no cost data) already ordered by name — the SPA
// renders what comes back in the order it arrives and never re-sorts or asks for
// anything else.
//
// `query` is the text the customer typed. Omit it (or pass an empty string) for
// the first page of the Company's items; the backend trims and length-caps it and
// Nexus owns the match (name, SKU and description, tokenized), so the SPA does no
// filtering of its own beyond narrowing the page it already holds — by the same
// token rule, so the two cannot disagree (shared util/search-tokens.ts).
//
// Not pricing data, so R4 would permit a short cache, but the pickers hold none:
// every mount revalidates against Nexus, so an item that reached Nexus is listed
// the next time a picker opens. A browser reload starts from an empty cache.
//
// `refreshFromWarehouse` is the picker's "check the warehouse for new items"
// click and nothing else: Nexus pulls the Company's new ArgoWMS items before it
// searches. Never send it on load, open, keystroke or timer — each one is
// traffic on the warehouse.

import type { CompanyItem, CompanyItemPage } from 'shared';

import { api } from './client.js';

// Answers the page AND whether Nexus cut it off at its row cap, so the picker can
// say the list continues past what it holds rather than ending silently.
export async function getCompanyItems(
  query?: string,
  opts: { refreshFromWarehouse?: boolean } = {},
): Promise<CompanyItemPage> {
  const trimmed = query?.trim();
  const res = await api.get<CompanyItemPage>('/companies/items', {
    params: {
      ...(trimmed ? { query: trimmed } : {}),
      ...(opts.refreshFromWarehouse === true ? { refresh_from_warehouse: 'true' } : {}),
    },
  });
  return {
    items: res.data.items,
    truncated: res.data.truncated === true,
    has_company: res.data.has_company === true,
  };
}

// Resolve specific selection keys, whatever the search text would have matched.
//
// What a picker needs to render a STORED pick: the key was frozen onto the quote
// at pick time, and nothing the customer might type is guaranteed to surface it
// again — the item may have been renamed, or simply sort past the page cap.
// Without this the picker's own trigger would read as empty on a saved quote,
// which reads as "your pick is gone".
//
// Keys are sent as one comma-joined param; the backend splits, dedupes and caps
// them. An empty list is NOT a request — it would read far-side as "no key
// filter" and answer with an unfiltered page — so callers must skip the fetch.
export async function getCompanyItemsByIds(item_ids: readonly string[]): Promise<CompanyItem[]> {
  const keys = item_ids.filter((k) => k.length > 0);
  if (keys.length === 0) return [];
  const res = await api.get<{ items: CompanyItem[] }>('/companies/items', {
    params: { item_ids: keys.join(',') },
  });
  return res.data.items;
}
